import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"

// Model Objects

export class CaskType extends ModelObjectWithDirtyCheck<CaskType> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    public isHidden: boolean = false;
    /**
    * Name
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public name: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.isHidden = jsonSerializedObject.isHidden;
          this.name = jsonSerializedObject.name;
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.isHidden = this.isHidden;
        toRet.name = this.name;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "CaskType.Id cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length !== 0 || "Name cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length <= 1000 || "Name has too many characters (1000)"},
    ];
}


export class CaskSubType extends ModelObjectWithDirtyCheck<CaskSubType> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    public isHidden: boolean = false;
    /**
    * Name
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public name: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.isHidden = jsonSerializedObject.isHidden;
          this.name = jsonSerializedObject.name;
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.isHidden = this.isHidden;
        toRet.name = this.name;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "CaskSubType.Id cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length !== 0 || "Name cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length <= 1000 || "Name has too many characters (1000)"},
    ];
}


export class CaskTypeService extends ServiceBase {

  public async getAllCaskTypes(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(CaskType)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.caskTypes.map((v: any) => new CaskType(v));;
    return this.makeJsonRequest("caskType/v1/getAllCaskTypes", req, false, extractor, opts);
  };

  public async getCaskType(caskTypeId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<CaskType> {
    const req = {
    tenantId:api.tenantId.toString(),
    caskTypeId:caskTypeId.toString(),
    };
    const extractor = (response:any) => new CaskType(response.caskType);;
    return this.makeJsonRequest("caskType/v1/getCaskType", req, false, extractor, opts);
  };

  public async getCaskTypes(caskTypeIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(CaskType)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    caskTypeIds:caskTypeIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.caskTypes.map((v: any) => new CaskType(v));;
    return this.makeJsonRequest("caskType/v1/getCaskTypes", req, false, extractor, opts);
  };

  public async saveCaskType(caskType: CaskType, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    caskType:caskType.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("caskType/v1/saveCaskType", req, true, extractor, opts);
  };

  public async setCaskTypeIsHidden(caskTypeId: Guid, hide: boolean, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    caskTypeId:caskTypeId.toString(),
    hide:hide,
    };
    const extractor = null;
    return this.makeJsonRequest("caskType/v1/setCaskTypeIsHidden", req, true, extractor, opts);
  };

  public async getAllCaskSubTypes(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(CaskSubType)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.caskSubTypes.map((v: any) => new CaskSubType(v));;
    return this.makeJsonRequest("caskType/v1/getAllCaskSubTypes", req, false, extractor, opts);
  };

  public async getCaskSubType(caskSubTypeId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<CaskSubType> {
    const req = {
    tenantId:api.tenantId.toString(),
    caskSubTypeId:caskSubTypeId.toString(),
    };
    const extractor = (response:any) => new CaskSubType(response.caskSubType);;
    return this.makeJsonRequest("caskType/v1/getCaskSubType", req, false, extractor, opts);
  };

  public async getCaskSubTypes(caskSubTypeIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(CaskSubType)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    caskSubTypeIds:caskSubTypeIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.caskSubTypes.map((v: any) => new CaskSubType(v));;
    return this.makeJsonRequest("caskType/v1/getCaskSubTypes", req, false, extractor, opts);
  };

  public async saveCaskSubType(caskSubType: CaskSubType, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    caskSubType:caskSubType.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("caskType/v1/saveCaskSubType", req, true, extractor, opts);
  };

  public async setCaskSubTypeIsHidden(caskSubTypeId: Guid, hide: boolean, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    caskSubTypeId:caskSubTypeId.toString(),
    hide:hide,
    };
    const extractor = null;
    return this.makeJsonRequest("caskType/v1/setCaskSubTypeIsHidden", req, true, extractor, opts);
  };
}

export const caskTypeService = new CaskTypeService();